/**
 * iter87.466.1 — UCL Playoff Standings bracket view, sibling of
 * `MlsPlayoffStandings.jsx`. 5 rounds (Knockout Round Play-offs →
 * Round of 16 → Quarter-finals → Semi-finals → Final). Every round
 * except the Final is a 2-leg aggregate tie — shown as leg-by-leg
 * scores plus the aggregate/advance result, not a game-win tally.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { TeamSwatch } from "@/components/dashboard/TeamSwatch";

const DECIDED_LABEL = { regulation: "Regulation", extra_time: "Extra Time", penalties: "Penalties" };

export default function UclPlayoffStandings({ tournamentId, teams }) {
    const [bracket, setBracket] = useState(null);

    const nameFor = (id) => (teams || []).find((t) => t.id === id)?.name || "TBD";
    const seedFor = (id) => (teams || []).find((t) => t.id === id)?.seed;

    useEffect(() => {
        if (!tournamentId) return;
        api.getUclBracket(tournamentId).then(setBracket).catch(() => setBracket(null));
    }, [tournamentId]);

    if (!bracket || !bracket.rounds?.length) {
        return (
            <div className="brutal-card p-4 sm:p-5 text-center text-zinc-500 text-xs uppercase tracking-widest" data-testid="ucl-standings-empty">
                Playoff bracket not synced yet
            </div>
        );
    }

    const aggregate = (s) => {
        const goalsFor = (id) => (s.games || []).reduce((sum, g) => {
            if (!g.played || g.score_a == null || g.score_b == null) return sum;
            return sum + (g.team_a_id === id ? g.score_a : g.team_b_id === id ? g.score_b : 0);
        }, 0);
        return { a: goalsFor(s.team_a_id), b: goalsFor(s.team_b_id) };
    };

    const TeamRow = ({ id, isWinner, isLoser, score }) => (
        <div className={`flex items-center justify-between px-2 py-2 rounded-lg border transition-colors ${isWinner ? "bg-cyber/10 border-cyber/30" : "border-transparent"}`}>
            <div className="flex items-center min-w-0 gap-2">
                <span className="inline-flex w-5 h-5 items-center justify-center rounded-full bg-white/[0.04] border border-white/[0.08] font-mono text-[9px] font-bold text-zinc-400 shrink-0">
                    {seedFor(id) ?? "–"}
                </span>
                <TeamSwatch name={nameFor(id)} sport="ucl" size="w-6 h-6 text-[9px]" />
                <span className={`text-sm font-semibold truncate flex-1 ${isWinner ? "text-cyber" : isLoser ? "text-zinc-500 line-through" : "text-zinc-200"}`}>
                    {nameFor(id)}
                </span>
            </div>
            {score != null && (
                <span className={`font-mono font-black text-sm tabular-nums ml-2 shrink-0 ${isWinner ? "text-white" : "text-zinc-500"}`}>
                    {score}
                </span>
            )}
        </div>
    );

    return (
        <div className="space-y-5" data-testid="ucl-playoff-standings">
            {bracket.rounds.map((r) => {
                const series = r.series || [];
                const isFinalRound = r.key === "final";
                if (!series.length) {
                    return (
                        <div key={r.key} data-testid={`ucl-standings-round-${r.key}`}>
                            <h4 className="font-display font-extrabold uppercase text-base sm:text-lg tracking-tight mb-3 text-white border-b border-white/[0.08] pb-2">
                                {r.label}
                            </h4>
                            <div
                                data-testid={`ucl-standings-round-${r.key}-tbd`}
                                className="brutal-card-light border border-dashed border-white/[0.08] p-4 rounded-lg text-center text-zinc-500 text-xs uppercase tracking-widest"
                            >
                                TBD — not drawn yet
                            </div>
                        </div>
                    );
                }
                return (
                    <div key={r.key} data-testid={`ucl-standings-round-${r.key}`}>
                        <h4 className="font-display font-extrabold uppercase text-base sm:text-lg tracking-tight mb-3 text-white border-b border-white/[0.08] pb-2">
                            {r.label}
                        </h4>
                        <div className="space-y-3">
                            {series.map((s) => {
                                const agg = aggregate(s);
                                const isFinal = s.status === "final";
                                const isOpen = s.status === "open" || s.status === "locked";
                                const gamesPlayed = (s.games || []).filter((g) => g.played).length;
                                return (
                                    <div
                                        key={s.id}
                                        data-testid={`ucl-standings-series-${s.id}`}
                                        className={`brutal-card-light border border-white/[0.08] p-3 rounded-lg flex flex-col gap-1.5 ${s.status === "tbd" ? "opacity-60 border-dashed" : ""}`}
                                    >
                                        <div className="flex items-center justify-between pb-2 mb-1 border-b border-white/[0.04]">
                                            <span className="text-[9px] uppercase tracking-widest text-zinc-500 font-bold">
                                                {isFinalRound ? "Final" : "2-Leg Tie"}
                                            </span>
                                            {s.status === "tbd" && <span className="text-[9px] uppercase tracking-widest text-zinc-500">TBD</span>}
                                            {isOpen && (
                                                <span className="text-[9px] uppercase tracking-widest font-bold flex items-center gap-1">
                                                    {gamesPlayed > 0 ? (
                                                        <span className="pill bg-heat text-white px-2 py-0.5 flex items-center gap-1">
                                                            <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                                                            {isFinalRound ? "LIVE" : `LEG ${gamesPlayed + 1} OF ${s.games?.length || 2}`}
                                                        </span>
                                                    ) : (
                                                        <span className="pill bg-volt text-black px-2 py-0.5">UPCOMING</span>
                                                    )}
                                                </span>
                                            )}
                                            {isFinal && <span className="text-[9px] uppercase tracking-widest text-zinc-500 font-bold">FINAL</span>}
                                        </div>
                                        {s.team_a_id && (
                                            <TeamRow id={s.team_a_id} isWinner={isFinal && s.advance_team_id === s.team_a_id}
                                                     isLoser={isFinal && s.advance_team_id !== s.team_a_id} score={isFinalRound ? null : agg.a} />
                                        )}
                                        {s.team_b_id && (
                                            <TeamRow id={s.team_b_id} isWinner={isFinal && s.advance_team_id === s.team_b_id}
                                                     isLoser={isFinal && s.advance_team_id !== s.team_b_id} score={isFinalRound ? null : agg.b} />
                                        )}
                                        {!s.team_a_id && !s.team_b_id && (
                                            <p className="text-xs text-zinc-500 py-2">Matchup not set yet</p>
                                        )}
                                        {isFinal && !isFinalRound && (
                                            <p data-testid={`ucl-standings-series-${s.id}-summary`} className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold mt-1 pt-2 border-t border-white/[0.04]">
                                                {nameFor(s.advance_team_id)} advances {Math.max(agg.a, agg.b)}-{Math.min(agg.a, agg.b)} on aggregate
                                            </p>
                                        )}
                                        {isFinal && isFinalRound && (
                                            <p data-testid={`ucl-standings-series-${s.id}-summary`} className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold mt-1 pt-2 border-t border-white/[0.04]">
                                                {nameFor(s.advance_team_id)} wins it · {DECIDED_LABEL[s.decided_by] || "Regulation"}{s.total_goals != null ? ` · ${s.total_goals} goals` : ""}
                                            </p>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
